// Import default food image
import defaultFoodImage from './assets/images/default_food_image.png';

// API URL
export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// WebSocket URL - Updated for Docker networking
export const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:8000';

// Frontend URL
export const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000';

// Default pagination limit
export const DEFAULT_LIMIT = 10;

// Image placeholder
export const IMAGE_PLACEHOLDER = defaultFoodImage;


export {
  formatDate,
  formatTime,
  formatDateTime,
} from './utils/dateUtils';

// ─── Order Status Configuration ───────────────────────────────────────────────
export const ORDER_STATUS_CONFIG = {
  pending:          { label: 'Pending',          color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)',  muiColor: 'warning' },
  confirmed:        { label: 'Confirmed',        color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)',  muiColor: 'primary' },
  preparing:        { label: 'Preparing',        color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)',  muiColor: 'info' },
  ready:            { label: 'Ready',            color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)',  muiColor: 'success' },
  delivered:        { label: 'Delivered',        color: '#06b6d4', bg: 'rgba(6, 182, 212, 0.1)',   muiColor: 'info' },
  completed:        { label: 'Completed',        color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)',  muiColor: 'success' },
  cancelled:        { label: 'Cancelled',        color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)',   muiColor: 'error' },
  awaiting_payment: { label: 'Awaiting Payment', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)',  muiColor: 'warning' },
};

const DEFAULT_ORDER_STATUS = { label: 'Unknown', color: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)', muiColor: 'default' };

export const getOrderStatusConfig = (status) => {
  const key = status?.toLowerCase();
  return ORDER_STATUS_CONFIG[key] || DEFAULT_ORDER_STATUS;
};

export const getOrderStatusColor = (status) => {
  const cfg = getOrderStatusConfig(status);
  return { color: cfg.color, bg: cfg.bg };
};

export const getOrderStatusLabel = (status) => {
  return getOrderStatusConfig(status).label;
};

// Legacy uppercase map (for components still using ORDER_STATUS_COLORS)
export const ORDER_STATUS_COLORS = Object.fromEntries(
  Object.entries(ORDER_STATUS_CONFIG).map(([k, v]) => [k.toUpperCase(), v.color])
);

// ─── Payment Status Configuration ─────────────────────────────────────────────
export const PAYMENT_STATUS_CONFIG = {
  pending:            { label: 'Payment Pending',   color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)',  muiColor: 'warning' },
  processing:         { label: 'Processing',        color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)',  muiColor: 'info' },
  completed:          { label: 'Paid',              color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)',  muiColor: 'success' },
  failed:             { label: 'Payment Failed',    color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)',   muiColor: 'error' },
  refunded:           { label: 'Refunded',          color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)',  muiColor: 'secondary' },
  partially_refunded: { label: 'Partially Refunded', color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)', muiColor: 'secondary' },
};

const DEFAULT_PAYMENT_STATUS = { label: 'Unknown', color: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)', muiColor: 'default' };

export const getPaymentStatusConfig = (status) => {
  const key = status?.toLowerCase();
  return PAYMENT_STATUS_CONFIG[key] || DEFAULT_PAYMENT_STATUS;
};

export const PAYMENT_STATUS_COLORS = Object.fromEntries(
  Object.entries(PAYMENT_STATUS_CONFIG).map(([k, v]) => [k.toUpperCase(), v.color])
);

// ─── Payment Method Configuration ─────────────────────────────────────────────
export const PAYMENT_METHOD_CONFIG = {
  cash:   { label: 'Cash',           color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)', border: 'rgba(16, 185, 129, 0.30)' },
  gcash:  { label: 'GCash',          color: '#0075ff', bg: 'rgba(0, 117, 255, 0.12)',  border: 'rgba(0, 117, 255, 0.30)' },
};

const DEFAULT_PAYMENT_METHOD = { label: 'Unknown', color: '#6b7280', bg: 'rgba(107, 114, 128, 0.12)', border: 'rgba(107, 114, 128, 0.30)' };

/** True when the order exposes a non-empty payment method (before config lookup). */
export const hasPaymentMethodValue = (method) => {
  if (method == null || method === '') return false;
  if (typeof method === 'object') {
    const t = method.type;
    return t != null && String(t).trim() !== '';
  }
  return String(method).trim() !== '';
};

export const getPaymentMethodConfig = (method) => {
  const key = (typeof method === 'string' ? method : method?.type)?.toLowerCase?.() ?? '';
  return PAYMENT_METHOD_CONFIG[key] || DEFAULT_PAYMENT_METHOD;
};

/** MUI `sx`: centered on top edge (half above card), solid accent + white label; modest corner radius. */
export const getPaymentMethodTopCenterBadgeSx = (cfg, overrides = {}) => {
  const safeCfg = cfg || DEFAULT_PAYMENT_METHOD;
  return {
    position: 'absolute',
    left: '50%',
    top: 0,
    zIndex: 2,
    transform: 'translate(-50%, -50%)',
    bgcolor: safeCfg.color,
    color: '#fff',
    border: 'none',
    borderRadius: { xs: '5px', sm: '6px' },
    fontWeight: 800,
    fontSize: { xs: '0.56rem', sm: '0.62rem', md: '0.68rem' },
    height: { xs: 24, sm: 26 },
    letterSpacing: { xs: '0.01em', sm: '0.02em' },
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    maxWidth: { xs: 'calc(100% - 16px)', sm: 'min(280px, calc(100% - 24px))' },
    '& .MuiChip-icon': {
      color: '#fff !important',
      fontSize: { xs: '0.6rem !important', sm: '0.68rem !important', md: '0.75rem !important' },
    },
    '& .MuiChip-label': {
      px: { xs: 0.6, sm: 0.85, md: 1 },
      py: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    ...overrides,
  };
};

// Table status colors
export const TABLE_STATUS_COLORS = {
  AVAILABLE: '#2ecc71', // Green
  OCCUPIED: '#e74c3c', // Red
  RESERVED: '#f39c12', // Orange
  MAINTENANCE: '#95a5a6', // Gray
};
